import React, { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { API, api, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Monogram, Wordmark } from "@/components/Logo";
import { Timer, Globe2, CalendarCheck, Smartphone, Package, Code2, ArrowRight, FileDown, ImageDown } from "lucide-react";

const NAVY = "#191A54", RED = "#E4232E", ORANGE = "#F2994A", MUTED = "#6B6F8F", SOFT = "#F3F4FA";
const ICONS = [Timer, Globe2, CalendarCheck, Smartphone, Package, Code2];
const LANGS = { fr: "Français", en: "English", es: "Español", de: "Deutsch", it: "Italiano", pt: "Português" };

const Mark = ({ size }) => (
  <span className="inline-flex items-center gap-[2.2mm]">
    <Monogram size={size} />
    <Wordmark className="text-white" style={{ fontSize: size * 0.78 }} />
  </span>
);

const Heading = ({ children }) => (
  <div className="mb-[3mm]">
    <p style={{ color: NAVY, fontSize: "11.5pt", fontWeight: 800 }}>{children}</p>
    <span className="block mt-[1mm] h-[0.9mm] w-[10mm]" style={{ background: RED }} />
  </div>
);

function Flyer({ tx }) {
  return (
    <div className="relative bg-white overflow-hidden flex flex-col" style={{ width: "148mm", height: "210mm", fontFamily: "Arial, Helvetica, sans-serif" }}>
      <header className="relative px-[11mm] pt-[12mm] pb-[9mm]" style={{ background: NAVY, height: "52mm" }}>
        <span className="absolute rounded-full" style={{ background: "#24266E", width: "68mm", height: "68mm", right: "-26mm", top: "-28mm" }} />
        <div className="relative"><Mark size={38} /></div>
        <h1 className="relative mt-[5mm] text-white" style={{ fontSize: "17pt", lineHeight: 1.2, fontWeight: 800, fontFamily: "inherit" }}>
          {tx.title}
        </h1>
        <p className="relative mt-[3mm]" style={{ color: ORANGE, fontSize: "11.5pt", fontWeight: 800 }}>{tx.subtitle}</p>
      </header>
      <section className="grid grid-cols-3 gap-[3mm] px-[11mm] pt-[7mm]">
        {tx.features.map(([title, text], i) => { const Icon = ICONS[i]; return (
          <div key={title} className="flex flex-col items-center text-center rounded-[2.5mm] px-[1.5mm] py-[2.5mm]" style={{ background: SOFT, height: "31mm" }}>
            <span className="flex items-center justify-center rounded-full" style={{ background: NAVY, width: "8.5mm", height: "8.5mm" }}>
              <Icon size={15} color="#fff" strokeWidth={2.2} />
            </span>
            <p className="mt-[1.5mm]" style={{ color: NAVY, fontSize: "8.4pt", fontWeight: 800, lineHeight: 1.12 }}>{title}</p>
            <p className="mt-[0.8mm]" style={{ color: "#383838", fontSize: "10pt", lineHeight: 1.12 }}>{text}</p>
          </div>
        ); })}
      </section>
      <section className="px-[11mm] pt-[6mm]">
        <Heading>{tx.how}</Heading>
        <div className="flex items-center gap-[1.5mm]">
          {tx.steps.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-[2mm] flex-1">
                <span className="shrink-0 flex items-center justify-center rounded-full text-white" style={{ background: RED, width: "8mm", height: "8mm", fontSize: "11pt", fontWeight: 800 }}>{i + 1}</span>
                <span style={{ color: "#2A2E4A", fontSize: "7pt", fontWeight: 800, lineHeight: 1.2 }}>{s}</span>
              </div>
              {i < 2 && <ArrowRight size={14} color={ORANGE} strokeWidth={2.6} className="shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      </section>
      <section className="px-[11mm] pt-[7mm]">
        <Heading>{tx.trust}</Heading>
        <div className="flex flex-wrap gap-[2mm]">
          {tx.sectors.map((s) => (
            <span key={s} className="inline-flex items-center gap-[1.5mm] rounded-full bg-white px-[3mm] py-[1mm]" style={{ border: `0.8pt solid ${NAVY}`, color: NAVY, fontSize: "7.4pt", fontWeight: 800 }}>
              <span className="rounded-full" style={{ background: RED, width: "1.6mm", height: "1.6mm" }} />{s}
            </span>
          ))}
        </div>
      </section>
      <footer className="mt-auto flex items-center justify-between px-[11mm]" style={{ background: NAVY, height: "32mm", borderTop: `1mm solid ${RED}` }}>
        <div>
          <Mark size={32} />
          <p className="mt-[3mm] text-white" style={{ fontSize: "9.5pt", fontWeight: 800 }}>contact@findloop.io</p>
          <p style={{ color: ORANGE, fontSize: "9.5pt" }}>findloop.io</p>
        </div>
        <div className="flex items-center gap-[3.5mm]">
          <span style={{ color: "#B9BCE0", fontSize: "6.2pt" }}>{tx.scan}</span>
          <span className="rounded-[2mm] bg-white p-[1.5mm]"><QRCodeSVG value="https://findloop.io" size={76} fgColor={NAVY} level="M" /></span>
        </div>
      </footer>
    </div>
  );
}

export default function ProspectusA5() {
  const ref = useRef(null);
  const [busy, setBusy] = useState("");
  const [lang, setLang] = useState("fr");
  const [tx, setTx] = useState(null);
  useEffect(() => { api(`/admin/prospectus-a5/texts?lang=${lang}`).then(setTx).catch((e) => toast.error(e.message)); }, [lang]);
  const save = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  async function pdf() {
    setBusy("pdf");
    try {
      const res = await fetch(`${API}/admin/prospectus-a5.pdf?lang=${lang}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error("Génération du PDF impossible");
      save(await res.blob(), `findloop-prospectus-A5-CMJN-${lang}.pdf`);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  }
  async function png() {
    setBusy("png");
    try {
      // Rendered server-side from the PDF → identical layout and logo.
      const res = await fetch(`${API}/admin/prospectus-a5.png?lang=${lang}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error("png");
      save(await res.blob(), `findloop-prospectus-A5-300dpi-${lang}.png`);
    } catch (e) { toast.error("Capture PNG impossible"); } finally { setBusy(""); }
  }
  return (
    <div className="space-y-5" data-testid="prospectus-a5-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h2 className="font-display text-lg font-bold text-findo-navy">Prospectus A5 (148 × 210 mm)</h2>
          <p className="text-sm text-slate-500">PDF vectoriel CMJN avec 3 mm de fond perdu · PNG 300 dpi (1748 × 2480 px)</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
        <select value={lang} onChange={(e) => setLang(e.target.value)} className="h-10 rounded-full border border-border bg-white px-4 text-sm font-semibold text-findo-navy" data-testid="prospectus-lang-select">
          {Object.entries(LANGS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Button onClick={pdf} disabled={!!busy} className="rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="prospectus-download-pdf">
          <FileDown size={16} className="mr-1.5" /> {busy === "pdf" ? "…" : "Télécharger en PDF"}
        </Button>
        <Button onClick={png} disabled={!!busy || !tx} variant="outline" className="rounded-full font-semibold border-findo-navy text-findo-navy bg-white hover:bg-findo-navy/5 hover:text-findo-navy" data-testid="prospectus-download-png">
          <ImageDown size={16} className="mr-1.5" /> {busy === "png" ? "…" : "Télécharger en PNG"}
        </Button>
        </div>
      </div>
      <div className="flex justify-center rounded-2xl bg-slate-100 p-6 overflow-auto">
        {tx ? <div ref={ref} className="shadow-2xl" data-testid="prospectus-a5-page" lang={tx.lang}><Flyer tx={tx} /></div> : <div className="h-[210mm]" />}
      </div>
    </div>
  );
}
